import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { AuthService } from "../auth.service";
import { environment } from "../../../../environments/environment";
import { BehaviorSubject, catchError, map, Observable, tap, throwError } from "rxjs";
import { AddCartProduct, CartError, StorageProducts, UpdateQuantity } from "./interfaces/cart.interface";
import { Product } from "../../../features/establishment-main/components/products/interfaces/products.interface";
import { CartData, GetCartProducsResponseC } from "./interfaces/get-cart.interface";
import { Store } from "@ngrx/store";
import { CartModel } from "../../../store/cart/cart.model";
import { setCart } from "../../../store/cart/cart.actions";
import { selectCartSubtotal, selectCartTotal } from "../../../store/cart/cart.selectors";
import { ProductData } from "../../../features/establishment-main/components/list-products/interfaces/list-products-new.interface";
import { SelectedAddressService } from "../selected-address.service";

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private cartProducts: StorageProducts[] = [];
  private _products = new BehaviorSubject<StorageProducts[]>([]);
  selectedAddressService = inject(SelectedAddressService);
  private apiUrl = `${environment.apiUrl}`;
  storageProductsObservable: any;

  constructor(private http: HttpClient,
    private authService: AuthService,
    private store: Store<{ cart: CartModel }>
  ) {
    this._products = new BehaviorSubject<StorageProducts[]>([]);

  }

  canAddToCart(product: Product | ProductData): { allowed: boolean; message?: string } {
    let currentItems = this._products.getValue();
    // currentItems logged

    if (!currentItems || currentItems.length === 0 || !currentItems[0]?.items?.length) {
      return { allowed: true };
    }

    const idofEstablishment = Number(product.pg_establishments_id ?? product.id_restaurant);
    const firstItem = currentItems[0].items[0];

    if (!firstItem) {
      return { allowed: true };
    }

    const cartEstablishmentId = firstItem.pg_establishments_id;

    if (idofEstablishment !== cartEstablishmentId) {
      // Establishment IDs compared
      return {
        allowed: false,
        message: 'Solo puedes agregar productos del mismo establecimiento. Por favor, vacía tu carrito primero.'
      };
    }
    return { allowed: true };
  }
  public addCartForApi(item: Product | ProductData): Observable<AddCartProduct> {
    if (!item) {
      return throwError(() => ({
        message: 'No hay productos seleccionados',
        code: 'EMPTY_CART'
      } as CartError));
    }

    if (!this.canAddToCart(item).allowed) {
      return throwError(() => ({
        message: 'No puedes agregar productos de diferentes establecimientos',
        code: 'DIFFERENT_ESTABLISHMENT'
      } as CartError));
    }



    if (!item.quantity || item.quantity <= 0) {
      return throwError(() => ({
        message: 'La cantidad debe ser mayor a 0',
        code: 'INVALID_QUANTITY'
      } as CartError));
    }
    const idAdressUser = this.selectedAddressService.getCurrentSelectedAddress()?.ad_id
    const cartData = {
      shopping: {
        items: [{
          product_id: item.pd_id.toString(),
          establishment: item.pg_establishments_id,
          quantity: item.quantity,
          variations: item.selectedVariations,
          complements: item.selectedComplements,
          address: idAdressUser
        }]
      }
    };
    return this.http.post<AddCartProduct>(`${this.apiUrl}/shopping/add`, cartData, {
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      }
    }).pipe(
      catchError((error) => {
        const cartError: CartError = {
          message: error.error?.message || 'Error al agregar al carrito',
          code: error.error?.code || 'API_ERROR',
          status: error.status
        };
        return throwError(() => cartError);
      })
    );
  }
  public getCartFromApi(): Observable<GetCartProducsResponseC> {
    if (!this.authService.getToken()) {
      throw new Error('No hay token');
    }

    return this.http.get<GetCartProducsResponseC>(`${this.apiUrl}/shopping/get`, {
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      }
    }).pipe(
      map((response:GetCartProducsResponseC) => {
        let storageProducts: CartData = {
          cart_id: 0,
          establishment_id: "",
          products: [],
          total: 0,
          establishment_name: ""
        };
        if (response?.data) {
          storageProducts = {
            cart_id: response.data.cart_id,
            establishment_name: response.data.establishment_name,
            establishment_id: response.data.establishment_id,
            products: response.data.products || [],
            total: response.data.total || 0
          }
        }
        // Siempre actualizar el store, incluso si no hay datos (carrito vacío)
        this.store.dispatch(setCart({ CartData: storageProducts }));
        return response;
      })
    );
  }
  getProducts() {
    return this.storageProductsObservable
  }

  getTotalItems(): Observable<number> {

    return this.store.select(state => state.cart.CartData?.products).pipe(
      map(products => {
        let count = 0;
        if (!products) {
          return 0;
        }
        products.forEach((product) => {
          if (product.quantity) {
            count += Number(product.quantity)

          }
        });
        return count;
      })
    );
  }
  getCostProducts(): Observable<number> {
    return this.store.select(selectCartSubtotal).pipe(
      map(subtotal => {
        return subtotal;
      })
    );
  }
  getTotalPurchase(): Observable<number> {
    return this.store.select(selectCartTotal).pipe(
      map(total => {
        return total;
      })
    );
  }
  clearCart(): Observable<{ success: boolean, message: string }> {

    if (!this.authService.getToken()) {
      throw new Error('No hay token');
    } else {
      return this.http.get<{ success: boolean, message: string }>(
        `${this.apiUrl}/shopping/remove/cart`,
        {
          headers: {
            Authorization: `Bearer ${this.authService.getToken()}`
          }
        }
      ).pipe(
        tap(() => {
          this.store.dispatch(setCart({
            CartData: {
              cart_id: 0,
              establishment_name: "",
              establishment_id: "",
              products: [],
              total: 0
            }
          }))
        }),
        catchError((error) => {
          console.error('Error clearing cart:', error);
          return throwError(() => error);
        })
      );
    }

  }
  removeItemFromCart(productId: string): Observable<{ success: boolean, message: string }> {
    const cartData = {
      shopping: {
        items: [
          {
            product_id: productId
          }
        ]
      }
    };
    return this.http.post<{ success: boolean, message: string }>(
      `${this.apiUrl}/shopping/remove/product`,
      cartData,
      {
        headers: {
          Authorization: `Bearer ${this.authService.getToken()}`
        }
      }
    ).pipe(
      tap((response) => {
        this.getCartFromApi().subscribe();
      }),
      catchError((error) => {
        console.error('Error removing item from cart:', error);
        return throwError(() => error);
      })
    );
  }
  updateItemQuantity(data: UpdateQuantity): Observable<{ sucess: string, message: string }> {

    return this.http.post<{ sucess: string, message: string }>(`${this.apiUrl}/shopping/update`, data, {
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      }
    }).pipe(
      tap((response) => {
        // Refrescar los datos del carrito después de actualizar la cantidad
        this.getCartFromApi().subscribe();
      }),
      catchError((error) => {
        console.error('Error updating item quantity:', error);
        return throwError(() => error);
      })
    )

  }
}
